import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  CalendarDays,
  ClipboardList,
  CornerDownLeft,
  FileBarChart,
  Image,
  LayoutGrid,
  type LucideIcon,
  MessageCircleQuestion,
  MessageSquare,
  Moon,
  Plus,
  Scale,
  Search,
  Settings,
  Sun,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import {
  ROUTES,
  UserRole,
  adminPath,
  lowerTr,
  type ClientListItem,
  type Paginated,
} from '@forever/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useThemePreference } from '@/lib/theme';
import { Avatar } from '@/components/ui';
import './command-palette.css';

type Command = {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  group: 'Git' | 'Eylem' | 'Danışan' | 'Görünüm';
  keywords?: string;
  run: () => void;
  /** Danışan satırlarında baş harf rozeti gösterilir. */
  avatarName?: string;
  avatarUrl?: string | null;
};

/**
 * Komut paleti.
 *
 * Yirmi ekranlı bir panelde gezinmenin en kısa yolu: Ctrl+K ile aç, yaz, gir.
 * Kenar çubuğunu büyütmek yerine bunu ekledik — arayüz sade kalıyor, güç
 * kullanıcının parmak ucunda duruyor.
 *
 * Danışan araması sunucudan geliyor; yazarken 200 ms bekleyip tek istek atıyor.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const isAdmin = user?.role === UserRole.ADMIN;
  const { mode, toggle } = useThemePreference();

  /* ------------------------------ açma / kapama ----------------------------- */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isCombo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isCombo) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
      setClients([]);
      return;
    }
    // Açılınca odak doğrudan arama alanına gider.
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(id);
  }, [open]);

  /* -------------------------------- danışan araması ------------------------------- */

  useEffect(() => {
    const term = query.trim();
    if (!open || term.length < 2) {
      setClients([]);
      return;
    }
    const controller = new AbortController();
    const id = window.setTimeout(async () => {
      try {
        const res = await api.get<Paginated<ClientListItem>>(
          ROUTES.clients.list,
          { q: term, pageSize: 5 },
          { signal: controller.signal },
        );
        setClients(res.items);
      } catch {
        // Arama başarısızsa palet yine de komutları göstermeye devam eder.
      }
    }, 200);

    return () => {
      controller.abort();
      window.clearTimeout(id);
    };
  }, [query, open]);

  /* --------------------------------- komutlar -------------------------------- */

  const go = useCallback(
    (path: string) => () => {
      setOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const commands = useMemo<Command[]>(() => {
    const nav: Command[] = [
      {
        id: 'g-dashboard',
        label: 'Genel bakış',
        icon: LayoutGrid,
        group: 'Git',
        run: go(adminPath.dashboard),
        keywords: 'pano özet ana sayfa',
      },
      {
        id: 'g-clients',
        label: 'Danışanlar',
        icon: Users,
        group: 'Git',
        run: go(adminPath.clients),
        keywords: 'hasta liste',
      },
      {
        id: 'g-plans',
        label: 'Diyet planları',
        icon: ClipboardList,
        group: 'Git',
        run: go(adminPath.plans),
        keywords: 'beslenme program',
      },
      {
        id: 'g-templates',
        label: 'Şablonlar ve kütüphane',
        icon: ClipboardList,
        group: 'Git',
        run: go(adminPath.templates),
        keywords: 'öğün hazır',
      },
      {
        id: 'g-messages',
        label: 'Mesajlar',
        icon: MessageSquare,
        group: 'Git',
        run: go(adminPath.messages),
        keywords: 'sohbet konuşma',
      },
      {
        id: 'g-questions',
        label: 'Bekleyen sorular',
        icon: MessageCircleQuestion,
        group: 'Git',
        run: go(adminPath.questions),
        keywords: 'yanıt soru',
      },
      {
        id: 'g-photos',
        label: 'Öğün fotoğrafları',
        icon: Image,
        group: 'Git',
        run: go(adminPath.photos),
        keywords: 'görsel değerlendirme',
      },
      {
        id: 'g-measurements',
        label: 'Ölçümler',
        icon: Scale,
        group: 'Git',
        run: go(adminPath.measurements),
        keywords: 'kilo tartı gelişim',
      },
      {
        id: 'g-calendar',
        label: 'Takvim',
        icon: CalendarDays,
        group: 'Git',
        run: go(adminPath.calendar),
        keywords: 'randevu görüşme',
      },
      {
        id: 'g-reports',
        label: 'Raporlar',
        icon: FileBarChart,
        group: 'Git',
        run: go(adminPath.reports),
        keywords: 'istatistik ciro',
      },
    ];

    if (isAdmin) {
      nav.push(
        {
          id: 'g-packages',
          label: 'Paket ve ödeme',
          icon: Wallet,
          group: 'Git',
          run: go(adminPath.packages),
          keywords: 'ücret tahsilat',
        },
        {
          id: 'g-rules',
          label: 'Bildirim kuralları',
          icon: Bell,
          group: 'Git',
          run: go(adminPath.notificationRules),
          keywords: 'hatırlatma',
        },
        {
          id: 'g-team',
          label: 'Ekip ve erişim',
          icon: Users,
          group: 'Git',
          run: go(adminPath.team),
          keywords: 'yetki rol',
        },
        {
          id: 'g-settings',
          label: 'İşletme ayarları',
          icon: Settings,
          group: 'Git',
          run: go(adminPath.settings),
          keywords: 'kurum çalışma saatleri',
        },
        {
          id: 'g-audit',
          label: 'İşlem geçmişi',
          icon: FileBarChart,
          group: 'Git',
          run: go(adminPath.audit),
          keywords: 'denetim log kayıt',
        },
      );
    }

    const actions: Command[] = [
      {
        id: 'a-new-client',
        label: 'Yeni danışan ekle',
        icon: UserPlus,
        group: 'Eylem',
        run: go(adminPath.newClient),
        keywords: 'kayıt aç hesap',
      },
      {
        id: 'a-new-plan',
        label: 'Yeni diyet planı hazırla',
        icon: Plus,
        group: 'Eylem',
        run: go(adminPath.newPlan),
        keywords: 'plan oluştur',
      },
      {
        id: 'a-new-appointment',
        label: 'Randevu ekle',
        icon: CalendarDays,
        group: 'Eylem',
        run: go(adminPath.calendar),
        keywords: 'görüşme planla',
      },
    ];

    const view: Command[] = [
      {
        id: 'v-theme',
        label: mode === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç',
        icon: mode === 'dark' ? Sun : Moon,
        group: 'Görünüm',
        keywords: 'tema karanlık aydınlık renk',
        run: () => {
          toggle();
          setOpen(false);
        },
      },
    ];

    const clientCommands: Command[] = clients.map((c) => ({
      id: `c-${c.id}`,
      label: c.fullName,
      hint: c.email,
      icon: Users,
      group: 'Danışan',
      avatarName: c.fullName,
      avatarUrl: c.avatarUrl,
      run: go(adminPath.client(c.id)),
    }));

    return [...clientCommands, ...nav, ...actions, ...view];
  }, [clients, go, isAdmin, mode, toggle]);

  /* ---------------------------------- süzme ---------------------------------- */

  const filtered = useMemo(() => {
    const term = lowerTr(query.trim());
    if (!term) return commands;
    return commands.filter((c) => {
      // Danışan sonuçları zaten sunucuda süzüldü; yerel süzme onları elemesin.
      if (c.group === 'Danışan') return true;
      const haystack = lowerTr(`${c.label} ${c.keywords ?? ''} ${c.hint ?? ''}`);
      return term.split(/\s+/).every((word) => haystack.includes(word));
    });
  }, [commands, query]);

  useEffect(() => setActive(0), [query, clients.length]);

  // Seçili satır görünürde kalsın.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      filtered[active]?.run();
    }
  };

  if (!open) return <PaletteHint onOpen={() => setOpen(true)} />;

  // Grup başlıklarını satır akışının içine yerleştiriyoruz.
  let lastGroup = '';

  return createPortal(
    <div className="cmdk-overlay" role="presentation" onClick={() => setOpen(false)}>
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Komut paleti"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cmdk__search">
          <Search size={18} aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Sayfa, danışan veya eylem ara…"
            aria-label="Komut ara"
            aria-controls="cmdk-list"
            aria-activedescendant={filtered[active] ? `cmdk-${filtered[active].id}` : undefined}
            role="combobox"
            aria-expanded="true"
          />
          <kbd className="cmdk__kbd">esc</kbd>
        </div>

        <div className="cmdk__list" id="cmdk-list" role="listbox" ref={listRef}>
          {filtered.length === 0 ? (
            <p className="cmdk__empty">Eşleşen bir şey bulunamadı.</p>
          ) : (
            filtered.map((cmd, index) => {
              const showGroup = cmd.group !== lastGroup;
              lastGroup = cmd.group;
              return (
                <div key={cmd.id}>
                  {showGroup ? <div className="cmdk__group">{cmd.group}</div> : null}
                  <button
                    id={`cmdk-${cmd.id}`}
                    type="button"
                    role="option"
                    aria-selected={index === active}
                    data-active={index === active}
                    className="cmdk__item"
                    onMouseMove={() => setActive(index)}
                    onClick={cmd.run}
                  >
                    {cmd.avatarName ? (
                      <Avatar name={cmd.avatarName} src={cmd.avatarUrl} size={24} />
                    ) : (
                      <cmd.icon size={17} aria-hidden />
                    )}
                    <span className="cmdk__label">{cmd.label}</span>
                    {cmd.hint ? <span className="cmdk__hint">{cmd.hint}</span> : null}
                    {index === active ? (
                      <CornerDownLeft size={14} aria-hidden className="cmdk__enter" />
                    ) : (
                      <ArrowRight size={14} aria-hidden className="cmdk__arrow" />
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="cmdk__footer">
          <span>
            <kbd className="cmdk__kbd">↑</kbd>
            <kbd className="cmdk__kbd">↓</kbd> gezin
          </span>
          <span>
            <kbd className="cmdk__kbd">↵</kbd> aç
          </span>
          <span className="cmdk__brand">Forever Deluxe</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Üst çubuktaki arama tetikleyicisi — paletin varlığını görünür kılar. */
function PaletteHint({ onOpen }: { onOpen: () => void }) {
  /*
   * `navigator.platform` kullanımdan kaldırıldı ve bazı tarayıcılarda boş
   * dönüyor. Önce standart `userAgentData.platform`'a bakıyoruz, yoksa
   * kullanıcı aracısına düşüyoruz.
   */
  const isMac = (() => {
    if (typeof navigator === 'undefined') return false;
    const modern = (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform;
    return /mac|iphone|ipad|ipod/i.test(modern || navigator.userAgent || '');
  })();
  return (
    <button type="button" className="cmdk-trigger" onClick={onOpen} aria-label="Komut paletini aç">
      <Search size={15} aria-hidden />
      <span>Ara…</span>
      <kbd className="cmdk__kbd">{isMac ? '⌘' : 'Ctrl'}</kbd>
      <kbd className="cmdk__kbd">K</kbd>
    </button>
  );
}
